/**
 * IndicatorAdapter.js
 * 
 * Implements the adapter pattern for handling different indicator data formats
 * from various API sources like TAAPI, Alpha Vantage, and others.
 */

import { normalizeIndicatorData } from './DataNormalizer';

/**
 * Base adapter class for standardizing indicator data
 */
class BaseIndicatorAdapter {
  constructor(options = {}) {
    this.options = {
      debug: false,
      ...options
    };
  }

  /**
   * Process raw data into a standardized format
   * 
   * @param {*} rawData - Raw data from API response
   * @returns {Array} - Normalized data array
   */
  process(rawData) {
    throw new Error('Method process() must be implemented by subclass');
  }

  /**
   * Helper method for logging
   * 
   * @param {string} message - Log message
   * @param {*} data - Optional data to log
   */
  log(message, data) {
    if (this.options.debug) {
      console.log(`[${this.constructor.name}] ${message}`, data);
    }
  }
}

/**
 * Adapter for TAAPI API indicator data
 */
export class TaapiAdapter extends BaseIndicatorAdapter {
  /**
   * Process TAAPI format data into standardized format
   * 
   * @param {*} rawData - Raw data from TAAPI API
   * @returns {Array} - Normalized data array
   */
  process(rawData) {
    this.log('Processing TAAPI data', rawData);

    if (!rawData) {
      return [];
    }

    // TAAPI format 1: Direct value
    if (typeof rawData === 'number') {
      return [{
        value: rawData,
        timestamp: Date.now(),
        metadata: {
          source: 'taapi',
          format: 'direct_value'
        }
      }];
    }

    // TAAPI format 2: Object with value property
    if (typeof rawData === 'object' && rawData.value !== undefined) {
      // If rawData.value is an array, it's actually format 5
      if (Array.isArray(rawData.value)) {
        this.log('Detected array in value property', rawData.value.length);
        return rawData.value.map(val => ({
          value: val,
          timestamp: Date.now(),
          metadata: {
            source: 'taapi',
            format: 'value_array_in_object'
          }
        }));
      }
      
      return [{
        value: rawData.value,
        timestamp: rawData.timestamp || Date.now(),
        metadata: {
          source: 'taapi',
          format: 'value_object'
        }
      }];
    }

    // TAAPI format 3: Array of values directly
    if (Array.isArray(rawData)) {
      // If the array elements have a "result" property with value, handle that case
      if (rawData.length > 0 && rawData[0]?.result?.value !== undefined) {
        this.log('Detected array of result objects', rawData.length);
        return rawData.map(item => ({
          value: item.result.value,
          timestamp: item.result.timestamp || Date.now(),
          metadata: {
            source: 'taapi',
            format: 'result_object_array'
          }
        }));
      }
      
      // If items in array are already objects with value/timestamp
      if (rawData.length > 0 && typeof rawData[0] === 'object' && rawData[0].value !== undefined) {
        this.log('Detected array of value objects', rawData.length);
        return rawData.map(item => ({
          value: item.value,
          timestamp: item.timestamp || Date.now(),
          metadata: {
            source: 'taapi',
            format: 'value_object_array'
          }
        }));
      }
      
      // If items in array are direct numeric values
      if (rawData.length > 0 && typeof rawData[0] === 'number') {
        this.log('Detected array of numeric values', rawData.length);
        return rawData.map(value => ({
          value: value,
          timestamp: Date.now(),
          metadata: {
            source: 'taapi',
            format: 'numeric_array'
          }
        }));
      }
    }

    // TAAPI format 4: Parallel arrays format
    if (rawData.result && 
        Array.isArray(rawData.result.value) && 
        Array.isArray(rawData.result.timestamp)) {
      
      const { value, timestamp } = rawData.result;
      return value.map((val, i) => ({
        value: val,
        timestamp: timestamp[i],
        metadata: {
          source: 'taapi',
          format: 'parallel_arrays'
        }
      }));
    }
    
    // TAAPI format 5: Object with result containing array
    if (rawData.result && Array.isArray(rawData.result)) {
      this.log('Detected result array format', rawData.result.length);
      return rawData.result.map(item => {
        if (typeof item === 'object' && item.value !== undefined) {
          return {
            value: item.value,
            timestamp: item.timestamp || Date.now(),
            metadata: {
              source: 'taapi',
              format: 'result_array_objects'
            }
          };
        } else if (typeof item === 'number') {
          return {
            value: item,
            timestamp: Date.now(),
            metadata: {
              source: 'taapi',
              format: 'result_array_numbers'
            }
          };
        }
        return null;
      }).filter(Boolean);
    }

    // Add specific debug info for TAAPI data structure before fallback
    if (rawData.result) {
      console.log('[TaapiAdapter] Unhandled TAAPI result format:', 
        typeof rawData.result, 
        Array.isArray(rawData.result) ? 'array' : '', 
        Object.keys(rawData)
      );
    }

    // Fallback to general normalizer
    return normalizeIndicatorData(rawData);
  }
}

/**
 * Adapter for Alpha Vantage API indicator data
 */
export class AlphaVantageAdapter extends BaseIndicatorAdapter {
  /**
   * Process Alpha Vantage format data into standardized format
   * 
   * @param {*} rawData - Raw data from Alpha Vantage API
   * @returns {Array} - Normalized data array
   */
  process(rawData) {
    this.log('Processing Alpha Vantage data', rawData);

    if (!rawData) {
      return [];
    }

    // Alpha Vantage typically returns time series data
    if (rawData['Meta Data'] && rawData['Technical Analysis']) {
      const indicatorType = Object.keys(rawData['Technical Analysis'])[0];
      const timeSeries = rawData['Technical Analysis'][indicatorType];
      
      return Object.entries(timeSeries).map(([date, values]) => ({
        value: parseFloat(values[indicatorType]),
        timestamp: new Date(date).getTime(),
        metadata: {
          source: 'alpha_vantage',
          format: 'time_series',
          date
        }
      })).sort((a, b) => a.timestamp - b.timestamp);
    }

    // Alpha Vantage time series data (OHLC format)
    if (rawData['Meta Data'] && (
      rawData['Time Series (Daily)'] || 
      rawData['Time Series (Weekly)'] || 
      rawData['Time Series (Monthly)'] ||
      rawData['Time Series (60min)'] ||
      rawData['Time Series (30min)'] ||
      rawData['Time Series (15min)'] ||
      rawData['Time Series (5min)'] ||
      rawData['Time Series (1min)']
    )) {
      const timeSeriesKey = Object.keys(rawData).find(key => key.startsWith('Time Series'));
      const timeSeries = rawData[timeSeriesKey];
      
      return Object.entries(timeSeries).map(([date, values]) => ({
        value: parseFloat(values['4. close']),
        timestamp: new Date(date).getTime(),
        open: parseFloat(values['1. open']),
        high: parseFloat(values['2. high']),
        low: parseFloat(values['3. low']),
        close: parseFloat(values['4. close']),
        volume: parseFloat(values['5. volume'] || values['6. volume'] || 0),
        metadata: {
          source: 'alpha_vantage',
          format: 'ohlc_time_series',
          date
        }
      })).sort((a, b) => a.timestamp - b.timestamp);
    }

    // Fallback to general normalizer
    return normalizeIndicatorData(rawData);
  }
}

/**
 * Adapter for Yahoo Finance API data
 */
export class YahooFinanceAdapter extends BaseIndicatorAdapter {
  /**
   * Process Yahoo Finance format data into standardized format
   * 
   * @param {*} rawData - Raw data from Yahoo Finance API
   * @returns {Array} - Normalized data array
   */
  process(rawData) {
    this.log('Processing Yahoo Finance data', rawData);

    if (!rawData) {
      return [];
    }

    // Yahoo Finance chart data format
    if (rawData.chart && rawData.chart.result && rawData.chart.result.length > 0) {
      const result = rawData.chart.result[0];
      const { timestamp, indicators } = result;
      
      if (!timestamp || !indicators) {
        return [];
      }

      // Process quote data (OHLCV)
      if (indicators.quote && indicators.quote.length > 0) {
        const quote = indicators.quote[0];
        const { open, high, low, close, volume } = quote;
        
        return timestamp.map((time, i) => ({
          value: close[i],
          timestamp: time * 1000, // Yahoo provides seconds, convert to ms
          open: open[i],
          high: high[i],
          low: low[i],
          close: close[i],
          volume: volume[i],
          metadata: {
            source: 'yahoo_finance',
            format: 'chart_result',
            interval: result.meta.dataGranularity,
            symbol: result.meta.symbol
          }
        }));
      }
      
      // Process just the indicator data if present
      if (indicators.adjclose || indicators.rsi || indicators.sma || indicators.ema) {
        const indicatorType = Object.keys(indicators).find(key => key !== 'quote');
        if (indicatorType && indicators[indicatorType].length > 0) {
          const indicatorData = indicators[indicatorType][0];
          const values = indicatorData[indicatorType] || indicatorData.adjclose;
          
          if (values) {
            return timestamp.map((time, i) => ({
              value: values[i],
              timestamp: time * 1000, // Yahoo provides seconds, convert to ms
              metadata: {
                source: 'yahoo_finance',
                format: 'indicator_data',
                type: indicatorType,
                interval: result.meta.dataGranularity,
                symbol: result.meta.symbol
              }
            }));
          }
        }
      }
    }

    // Fallback to general normalizer
    return normalizeIndicatorData(rawData);
  }
}

/**
 * Factory for creating adapters based on data source
 */
export class IndicatorAdapterFactory {
  /**
   * Create an appropriate adapter for the data source
   * 
   * @param {string} source - Data source identifier
   * @param {Object} options - Adapter options
   * @returns {BaseIndicatorAdapter} - The adapter instance
   */
  static createAdapter(source, options = {}) {
    switch (source.toLowerCase()) {
      case 'taapi':
        return new TaapiAdapter(options);
      case 'alpha_vantage':
      case 'alphavantage':
        return new AlphaVantageAdapter(options);
      case 'yahoo':
      case 'yahoo_finance':
        return new YahooFinanceAdapter(options);
      default:
        console.warn(`No specific adapter found for source: ${source}, using TAAPI adapter`);
        return new TaapiAdapter(options);
    }
  }

  /**
   * Process data through the appropriate adapter
   * 
   * @param {*} data - Raw data to process
   * @param {string} source - Data source identifier
   * @param {Object} options - Adapter options
   * @returns {Array} - Normalized data array
   */
  static process(data, source, options = {}) {
    const adapter = this.createAdapter(source, options);
    return adapter.process(data);
  }
}

export default IndicatorAdapterFactory;
